import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber, injectIntl, intlShape } from 'react-intl';
import { FormBuilder } from 'shared-components/forms/form';
import { Helpers } from 'shared-components/helpers/helpers';
import Moment from 'moment';
import { result, get, isEmpty, find, filter, map } from 'lodash';
import classnames from 'classnames';
import { Schedule } from '../elements-forms/schedule';
import { LightbulbTip } from '../app-host-calendar/shared-component/lightbulb-tip';
import { DateTimeMain } from '../elements-date-time/date-time-main';
import BorderedButton from '../elements-buttons/bordered-button';
import { createOfferQuote, hostUpdateQuote } from '../app-main/actions/contracts';
import { updateAddOnsStep } from '../app-main/actions/add-ons';
import { updatePayload } from '../app-main/actions/payload';
import { DayRateDiscountDisplay } from './day-rate-discount-display'
import currencySym from 'currency-symbol-map';
import { getCurrentSpaceRentalContract } from './utils';

class CustomOfferBookingDetailsPage extends React.Component {
  constructor (props, context) {
    super(props, context);

    this.state = {
      isDisableSpinning: true,
      disableButton: false,
      dateTimeError: '',
      attendeeError: '',
      displayError: '',
      updatedSubtotal: null,
      updatedCleaning: null,
      updatedAttendees: null
    };

    this.customOfferFormRef = {};
    this.getCurrentSchedule = this.getCurrentSchedule.bind(this);
    this.hasActivatedCleaning = this.hasActivatedCleaning.bind(this);
    this.attendeeConstraint = this.attendeeConstraint.bind(this);
    this.getValueFromInput = this.getValueFromInput.bind(this);
    this.validateInput = this.validateInput.bind(this);
    this.prepareScheduleUpdate = this.prepareScheduleUpdate.bind(this);
    this.prepareCustomOffer = this.prepareCustomOffer.bind(this);
    this.prepareCustomOfferCb = this.prepareCustomOfferCb.bind(this);
    this.postOffer = this.postOffer.bind(this);
    this.postOfferCb = this.postOfferCb.bind(this);
    this.scheduleInput = this.scheduleInput.bind(this);
    this.formInputs = this.formInputs.bind(this);
    this.formProps = this.formProps.bind(this);
  }

  componentDidMount () {
    window.scrollTo(0, 0);
    this.props.scrollToTop();
  }

  getCurrentSchedule () {
    const { payloadData } = this.props;
    const { updatedSchedule } = this.state;

    const currentSpaceRental = getCurrentSpaceRentalContract(this.props.contract);
    const booking = isEmpty(currentSpaceRental)
      ? this.props.booking
      : get(currentSpaceRental, 'meta.booking', {});

    const payloadBooking = payloadData ? find((payloadData.items || {}), { type: 'BOOKING' }) : null;
    const payloadSchedule = get(payloadBooking, 'info.schedule', null);
    const bookingSchedule = booking.schedule;
    const scheduleInBooking = !isEmpty(bookingSchedule) ? bookingSchedule : [];

    const currentSchedule = scheduleInBooking.map((hash) => {
      let returnVal;
      if (!isEmpty(hash)) {
        const date = hash.start.date;
        const day = Moment(date.m + '-' + date.d + '-' + date.y, 'MM-DD-YYYY');
        if (day.isSameOrAfter(Moment().startOf('day'))) {
          returnVal = hash;
        }
      }
      return returnVal;
    });

    // always prefer the updatedSchedule or payload schedule && filter for current dates
    const schedule = updatedSchedule || payloadSchedule || currentSchedule;
    const filteredSchedule = filter(schedule, (sched) => { return !!sched; });
    const finalSchedule = filteredSchedule.length ? filteredSchedule : [{ }];
    return finalSchedule;
  }

  getValueFromInput (value, cb) {
    const { updatedSubtotal, updatedCleaning } = this.state;

    // don't make quote call if values haven't changed
    if (value.subtotal === updatedSubtotal || value.cleaning_fee === updatedCleaning) {
      return;
    }

    this.prepareCustomOffer(value);
    cb();
  }

  validateInput (errorData, validState) {
    return validState(errorData);
  }

  hasActivatedCleaning () {
    const currentSpaceRental = getCurrentSpaceRentalContract(this.props.contract);

    const listing = isEmpty(currentSpaceRental)
      ? get(this.props, 'booking.listing', {})
      : get(currentSpaceRental, 'meta.listing', {});

    return 'ACTIVATED' === get(listing, 'cleaning_fee.obj_state', '');
  }

  attendeeConstraint (value) {
    let attendeeError;
    if ('0' === value || 100000 <= value) {
      attendeeError = this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.number_attendees', defaultMessage: 'The number of attendees must be between {min} and {max}' }, { min: this.props.intl.formatNumber(1), max: this.props.intl.formatNumber(100000) });
    }

    return this.setState({ displayError: '', attendeeError: attendeeError || '' });
  }

  prepareScheduleUpdate (schedule) {
    if (!isEmpty(schedule[0])) {
      this.prepareCustomOffer({ schedule });
    }
  }

  createPayload (inputObj) {
    const { booking, isHostUpdate } = this.props;
    const { cleaningFee, schedule, subtotal, attendees } = inputObj;
    const controlId = isHostUpdate ? { control_id: booking._id } : {};

    let addtl_charge_lis;
    if (this.hasActivatedCleaning()) {
      addtl_charge_lis = [
        {
          index: 0,
          update: {
            charge_amount: cleaningFee,
            charge_str: 'Cleaning',
            apply_to: 'both_subtotals',
            apply_as: 'FLAT_RATE',
            value: cleaningFee,
            type: 'CLEANING_FEE'
          }
        }
      ];
    }

    const payload = {
      ...controlId,
      host_sso_id: get(booking, 'host.ssoid'),
      type: 'OFFER',
      guest_sso_id: get(booking, 'guest.ssoid'),
      items: [
        {
          type: 'BOOKING',
          info: {
            ...(isEmpty(schedule[0]) && { ignore_schedule: true }),
            ...(!isEmpty(schedule[0]) && { schedule }),
            ...(attendees && { number_of_guests: attendees }),
            addtl_charge_lis,
            custom_subtotal_amount: subtotal,
            booking_id: booking._id
          }
        }
      ]
    };

    return payload;
  }

  prepareCustomOffer (customInput) {
    const { accessToken, dispatch, payloadData, booking, isHostUpdate } = this.props;
    const { updatedSubtotal, updatedCleaning, updatedSchedule, updatedAttendees } = this.state;

    const isCleaningFee = find(booking.addtl_charges, { type: 'CLEANING_FEE' });
    const bookingCleaningFee = isCleaningFee ? parseFloat(isCleaningFee.value).toFixed(2) : 0.00;

    const payloadBooking = find((payloadData.items || {}), { type: 'BOOKING' });
    const payloadSubtotal = get(payloadBooking, 'info.custom_subtotal_amount', null);
    const payloadCleaningFee = get(payloadBooking, 'info.addtl_charge_lis[0].update.charge_amount', null);
    const payloadAttendees = get(payloadBooking, 'info.number_of_guests', null);

    if (customInput) {
      const payload = this.createPayload({
        attendees: Number(customInput.number_of_guests || updatedAttendees || payloadAttendees || booking.number_of_guests),
        cleaningFee: Number(customInput.cleaning_fee || updatedCleaning || payloadCleaningFee || bookingCleaningFee),
        schedule: customInput.schedule || updatedSchedule,
        subtotal: Number(customInput.subtotal || updatedSubtotal || payloadSubtotal || booking.subtotal_amount)
      });

      this.setState({
        ...(customInput.number_of_guests && { updatedAttendees: customInput.number_of_guests }),
        ...(customInput.subtotal && { updatedSubtotal: customInput.subtotal }),
        ...(customInput.cleaning_fee && { updatedCleaning: customInput.cleaning_fee }),
        ...(!isEmpty(customInput.schedule) && { updatedSchedule: customInput.schedule }),
        displayError: '',
        dateTimeError: ''
      });

      if (!isEmpty(payload)) {
        if (isHostUpdate) {
          return dispatch(hostUpdateQuote(payload, booking._id, accessToken, (response) => {
            this.prepareCustomOfferCb(response);
          }));
        }

        dispatch(createOfferQuote(payload, accessToken, (response) => {
          this.prepareCustomOfferCb(response);
        }));
      }
    }
  }

  prepareCustomOfferCb (response) {
    if (200 !== response.statusCode) {
      const errorBody = get(response, 'body.body', {});
      // display calendar conflict errors
      if ((errorBody).errors) {
        const conflictReason = get(errorBody, 'errors[0].conflict', null);
        const invalidReason = get(errorBody, 'errors[0].invalid', null);
        const reason = conflictReason || invalidReason;

        const errorMsg = map(reason.split('\n'), (item) => (
          <span>
            {item}
            <br />
          </span>
        ));
        return this.setState({ dateTimeError: errorMsg });
      }

      return this.setState({ displayError: this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.something_wrong', defaultMessage: 'Something went wrong, please try again.' }) });
    }
  }

  postOffer (e, formCb) {
    const { dispatch, isEditOffer, updatePayloadAddOns, payloadData, isHostUpdate, contract } = this.props;
    const { dateTimeError } = this.state;
    if (dateTimeError) { return; }

    const attendees = Number(result(this.customOfferFormRef.attendees, 'getValue', null));
    const schedule = result(this.customOfferFormRef.dateTimeMain, 'getLatestValue', null);
    const subtotal = Number(result(this.customOfferFormRef.subtotal, 'getValue', null));

    const cleaningFeeInput = this.customOfferFormRef.cleaningFee;
    const cleaningFee = cleaningFeeInput ? Number(result(this.customOfferFormRef.cleaningFee, 'getValue', null)) : null;

    const payload = this.createPayload({ attendees, schedule, subtotal, cleaningFee });

    // if in edit offer flow or navigating back after adding items, populate payload
    // with addons from the offer when continuing onto the next page (add ons page)
    if (isEditOffer || payloadData) {
      updatePayloadAddOns(payload);
    } else {
      dispatch(updatePayload(payload));
    }

    if (!contract.error) {
      analyticsIntegrations.customOfferStepCompleted(payload, 'booking_details', isEditOffer, isHostUpdate);
      formCb();
    }
  }

  postOfferCb () {
    const { dispatch } = this.props;
    dispatch(updateAddOnsStep(2));
  }

  scheduleInput () {
    const currentSpaceRental = getCurrentSpaceRentalContract(this.props.contract);
    const booking = isEmpty(currentSpaceRental)
      ? this.props.booking
      : get(currentSpaceRental, 'meta.booking', {});

    const timezone = get(booking, 'start.timezone');
    const timeZoneId = timezone ? timezone.id : '';
    const scheduleFromBooking = booking.schedule;
    const currentSchedule = this.getCurrentSchedule();
    const isMobile = Helpers.isMobile();
    const listing = get(booking, 'listing', {});
    return (
      <div data-testing-id="schedule-input">
        <div className="col-xs-12 col-sm-6 margin-lg-bottom">
          <div className="label" style={{ marginTop: 0 }}>
            <FormattedMessage
              id='inbox.custom_offer_booking_details_page.current'
              defaultMessage='Current'
            />
          </div>
          <div className="date-time-display">
            <Schedule timeZoneId={timeZoneId} schedule={scheduleFromBooking} />
          </div>
        </div>
        <div className="col-xs-12 col-sm-6">
          <div className="label new-time-label">
            <FormattedMessage
              id='inbox.custom_offer_booking_details_page.new'
              defaultMessage='New'
            />
          </div>
          <DateTimeMain
            calendarPlacement="bottom"
            ref={ref => { this.customOfferFormRef.dateTimeMain = ref; }}
            actionFn={this.prepareScheduleUpdate}
            scheduleFromProps={currentSchedule}
            optional={false}
            multiday
            isLabel={false}
            ispare
            spaceId={booking.space_id}
            excludeBookingIds={[booking._id]}
            isHost
            country={ get(listing, 'country', '') }
          />
        </div>
        <div className="col-xs-12 help-block inline-help text-danger text-right">{this.state.dateTimeError}</div>
        <div className="col-xs-12">
          <hr />
        </div>
      </div>
    );
  }

  formInputs () {
    const { contract, payloadData, currency } = this.props;
    const { attendeeError, displayError } = this.state;

    const currentSpaceRental = getCurrentSpaceRentalContract(contract);
    const booking = isEmpty(currentSpaceRental)
      ? this.props.booking
      : get(currentSpaceRental, 'meta.booking', {});

    const payloadBooking = payloadData ? find((payloadData.items || {}), { type: 'BOOKING' }) : null;
    const payloadSubtotal = get(payloadBooking, 'info.custom_subtotal_amount', null);
    const payloadCleaningFee = get(payloadBooking, 'info.addtl_charge_lis[0].update.charge_amount', null);
    const payloadAttendees = get(payloadBooking, 'info.number_of_guests', null);

    const isFandB = 'FANDB' === booking.type;
    const taxPercentage = booking.sales_tax_percent;
    const tax = booking.sales_tax;
    const tipPercentage = booking.gratuity_percent;
    const tip = booking.gratuity;

    const bookingSubtotal = isFandB ? booking.base_subtotal : booking.subtotal_amount;
    const isCleaningFee = find(booking.addtl_charges, { type: 'CLEANING_FEE' });
    const cleaningFee = isCleaningFee ? parseFloat(isCleaningFee.value).toFixed(2) : 0.00;
    const isDayRateDiscount = filter(booking.discounts, { type: 'DAY_RATE_DISCOUNT' });

    const currentSpaceRentalSubtotal = get(currentSpaceRental, 'amount.000000000000000000000000', 0);
    const total = isFandB ? booking.subtotal_amount : parseFloat(currentSpaceRentalSubtotal).toFixed(2);

    const contractQuote = get(contract, 'quote');
    const updatedSpaceRental = contractQuote ? find(contractQuote.children, { type: 'SPACE_RENTAL' }) : {};
    const updatedTotal = parseFloat(get(updatedSpaceRental, 'amount.000000000000000000000000', 0)).toFixed(2);
    const currencySymbol = currencySym(currency);

    const inputs = [
      [{ custom: this.scheduleInput }],
      <div className="col-xs-12 col-sm-6">
        <h6>
          <FormattedMessage
            id='inbox.custom_offer_booking_details_page.attendees'
            defaultMessage='Attendees'
          />
        </h6>
        <div className="label">
          <FormattedMessage
            id='inbox.custom_offer_booking_details_page.current_people'
            defaultMessage='Current'
          />
        </div>
        <p className="current-attendees">
          <FormattedMessage
            id='inbox.custom_offer_booking_details_page.value_people'
            defaultMessage='{value} people'
            values={{
              value: booking.number_of_guests
            }}
          />
        </p>
      </div>,
      [
        {
          inputFieldName: 'attendees',
          elementID: 'number_of_guests',
          ref: ref => { this.customOfferFormRef.attendees = ref; },
          colSize: 'col-xs-12 col-sm-6 pull-right attendee-input',
          label: this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.new_label', defaultMessage: 'new' }),
          labelSize: 'col-xs-12',
          optional: false,
          type: 'number',
          actionFn: this.getValueFromInput,
          actionCb: this.validateInput,
          customConstraints: this.attendeeConstraint,
          defaultVal: payloadAttendees || booking.number_of_guests,
          helperText: attendeeError,
          errorMessage: this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.enter_number', defaultMessage: 'Please enter a number' })
        }
      ],
      <div className="col-xs-12 subtotal-label-container">
        <hr />
        <h6>
          <FormattedMessage
            id='inbox.custom_offer_booking_details_page.space_rental'
            defaultMessage='Space rental'
          />
        </h6>
        { isFandB
        && <div className="col-xs-12 no-gutter">
          <div className="lightbulb-tip">
            <LightbulbTip description={ this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.food_bar', defaultMessage: 'Food & Bar items as well as tax and tip can now be included as an add-on on the following page' }) } />
          </div>
        </div>
        }
        <div className="col-xs-12 col-sm-6 no-gutter-left subtotal-line">
          <div className="label" style={{ marginBottom: 16 }}>
            {isFandB
              ? <FormattedMessage id='inbox.custom_offer_booking_details_page.food_min' defaultMessage='Food & Bar tab minimum' />
              : <FormattedMessage id='inbox.custom_offer_booking_details_page.food_current' defaultMessage='Current' />
            }
          </div>
          <span className="current-subtotal">
            <FormattedMessage
              id='inbox.custom_offer_booking_details_page.subtotal'
              defaultMessage='Subtotal'
            />
          </span>
          <p className="pull-right text-gray-dark">
            <FormattedNumber value={bookingSubtotal} currency={currency} style={'currency'} />
          </p>
          { isFandB
          && <div data-testing-id="tax-tip-container">
            <div className="col-xs-12 no-gutter">
              <span className="current-subtotal">
                <FormattedMessage
                  id='inbox.custom_offer_booking_details_page.tax'
                  defaultMessage='Tax {tax}'
                  values={{
                    tax: <FormattedNumber value={taxPercentage / 100} style='percent' />
                  }}
                />
              </span>
              <p className="pull-right text-gray-dark" data-testing-id="tax-value">
                <FormattedNumber value={tax} currency={currency} style={'currency'} />
              </p>
            </div>
            <div className="col-xs-12 no-gutter">
              <span className="current-subtotal">
                <FormattedMessage
                  id='inbox.custom_offer_booking_details_page.tip'
                  defaultMessage='Tip {tip}'
                  values={{
                    tip: <FormattedNumber value={tipPercentage / 100} style='percent' />
                  }}
                />
              </span>
              <p className="pull-right text-gray-dark" data-testing-id="tip-value">
                <FormattedNumber value={tip} currency={currency} style={'currency'} />
              </p>
            </div>
          </div>
          }
        </div>
      </div>,
      [
        {
          inputFieldName: 'custom_offer_subtotal',
          colSize: `col-xs-12 col-sm-6 pull-right update-input ${isFandB ? 'fandb-booking' : ''}`,
          label: this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.new_space', defaultMessage: 'New{space}' },
            { space: isFandB
              ? ' ' + this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.rental_only', defaultMessage: '(space rental only)' })
              : ''
            }),
          ref: ref => { this.customOfferFormRef.subtotal = ref; },
          elementID: 'subtotal',
          optional: false,
          labelSize: 'col-xs-12',
          type: 'number',
          addOn: currencySymbol,
          actionFn: this.getValueFromInput,
          actionCb: this.validateInput,
          defaultVal: Number(payloadSubtotal || booking.subtotal_amount).toFixed(2),
          customConstraints: () => { },
          errorMessage: this.props.intl.formatMessage({
            id: 'inbox.custom_offer_booking_details_page.enter_amount',
            defaultMessage: 'A {currency}1 minimum is required to book on Peerspace'
          }, { currency: currencySymbol })
        }
      ],
      ...(this.hasActivatedCleaning() ? [
        <div className="col-xs-12 col-sm-6 current-cleaning">
          <h6 className="margin-lg-bottom visible-xs" style={{ marginBottom: 16 }}>Current</h6>
          <span className="text-gray-dark">
            <FormattedMessage
              id='inbox.custom_offer_booking_details_page.cleaning'
              defaultMessage='Cleaning'
            />
          </span>
          <p className="pull-right text-gray-dark" data-testing-id="cleaning-fee-value">
            <FormattedNumber value={cleaningFee} currency={currency} style={'currency'} />
          </p>
        </div>,
        [
          {
            inputFieldName: 'default_input_number',
            colSize: 'col-xs-12 col-sm-6 pull-right cleaning-input',
            ref: ref => { this.customOfferFormRef.cleaningFee = ref; },
            elementID: 'cleaning_fee',
            label: this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.new_cleaning_label', defaultMessage: 'New' }),
            labelSize: 'col-xs-12',
            optional: false,
            type: 'number',
            addOn: currencySymbol,
            actionFn: this.getValueFromInput,
            actionCb: this.validateInput,
            defaultVal: Number(payloadCleaningFee || cleaningFee).toFixed(2),
            customConstraints: () => { },
            errorMessage: this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.enter_amount_cleaning', defaultMessage: 'Please enter an amount.' })
          }
        ]
      ] : []),
      <div className="col-xs-12 total-line">
        <hr className={classnames({ 'no-top-margin': this.hasActivatedCleaning() })} />
        { !isEmpty(isDayRateDiscount) ? <div data-testing-id="dr-discount-container"><DayRateDiscountDisplay bookingObj={booking} /></div> : null }
        <div className="margin-lg-top">
          <div className="col-xs-12 col-sm-6 no-gutter-left current-total-line">
            <h6 className="margin-lg-bottom visible-xs">
              <FormattedMessage
                id='inbox.custom_offer_booking_details_page.current_total'
                defaultMessage='Current'
              />
            </h6>
            <span className="h6 text-gray-dark">
              <FormattedMessage
                id='inbox.custom_offer_booking_details_page.total'
                defaultMessage='Total'
              />
            </span>
            <p className="pull-right heavy text-gray-dark" data-testing-id="total-value">
              <FormattedNumber value={total} currency={currency} style={'currency'} />
            </p>
          </div>
          <div className="col-xs-12 col-sm-6 no-gutter">
            <h6 className="margin-lg-bottom visible-xs">
              <FormattedMessage
                id='inbox.custom_offer_booking_details_page.new_total'
                defaultMessage='New'
              />
            </h6>
            <span className="h6 text-gray-dark total-label">
              <FormattedMessage
                id='inbox.custom_offer_booking_details_page.total_new'
                defaultMessage='Total'
              />
            </span>
            <p className="pull-right heavy text-gray-dark" data-testing-id="updated-total-value">
              <FormattedNumber value={updatedTotal} currency={currency} style={'currency'} />
            </p>
          </div>
          <div className="help-block text-danger text-right" data-testing-id="contract-error">
            { (contract.error && <FormattedMessage id="inbox.custom_offer_booking_details_page.something_wrong_contract" defaultMessage="Something went wrong, please try again."/>)
            || displayError }
          </div>
        </div>
      </div>
    ];

    return inputs;
  }

  formProps () {
    const { isDisableSpinning, disableButton } = this.state;
    const formProps = {
      formStyle: 'standard',
      formname: 'bookings-update-form',
      isButtonDisabled: disableButton,
      isDisableSpinning,
      extended: true,
      scrollToError: true,
      formGroups: this.formInputs(),
      buttonText: this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.continue_button', defaultMessage: 'Continue to Add-ons' }),
      containerClass: 'form-btn-container',
      btnColSize: 'no-gutter addons-btn-container',
      buttonClass: 'btn classic btn-primary btn-form',
      submitAction: this.postOffer,
      submitCallback: this.postOfferCb,
      inputRefs: this.customOfferFormRef
    };

    return formProps;
  }

  render () {
    const { redirectToInboxDetails } = this.props;

    return (
      <div className="booking-details-step flex-one">
        <div className="booking-details-panels">
          <div className="host-update-form-container flex column">
            <h6 className="margin-lg-top">
              <FormattedMessage
                id='inbox.custom_offer_booking_details_page.date_time'
                defaultMessage='Date and Time'
              />
            </h6>
            <FormBuilder {...this.formProps()} />
          </div>
          <hr className="col-xs-12 margin-lg-top no-gutter" style={{ marginBottom: 12 }} />
          <BorderedButton
            className="col-xs-2 margin-md-top cancel-btn btn-default"
            buttonText={ this.props.intl.formatMessage({ id: 'inbox.custom_offer_booking_details_page.button_text', defaultMessage: 'Cancel' }) }
            onClick={redirectToInboxDetails}
          />
        </div>
      </div>
    );
  }
}

CustomOfferBookingDetailsPage.propTypes = {
  booking: PropTypes.object,
  contract: PropTypes.object,
  accessToken: PropTypes.string,
  dispatch: PropTypes.func,
  payloadData: PropTypes.object,
  redirectToInboxDetails: PropTypes.func,
  currency: PropTypes.string,
  isEditOffer: PropTypes.bool,
  updatePayloadAddOns: PropTypes.func,
  scrollToTop: PropTypes.func,
  isHostUpdate: PropTypes.bool,
  intl: intlShape
};

export default injectIntl(CustomOfferBookingDetailsPage);
